<template>
  <div class="table-responsive">
    <b-table
      striped
      hover
      fixed
      head-variant="dark"
      show-empty
      responsive
      :busy="loading"
      :empty-text="emptyText"
      :fields="tableFields"
      :items="canRead(access) ? rows : []"
      style="border-radius: 10px;"
    >
      <template v-slot:table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner variant="success" size="sm" class="align-middle"></b-spinner>
        </div>
      </template>
      <template v-slot:cell(action)="row">
        <div class="text-center">
          <button-options
            :access="access"
            :hideAdd="true"
            :hideDelete="!allowDelete"
            :hideDetail="true"
            :hideEdit="false"
            @onEditClicked="$emit('edit', row.item)"
            @onDeleteClicked="$emit('delete', row.item)"
          />
          <slot name="row-actions" :row="row.item" />
        </div>
      </template>
      <template
        v-for="column in badgeColumns"
        v-slot:[`cell(${column.key})`]="row"
      >
        <b-badge :key="column.key" :variant="column.variant ? column.variant(row.item) : 'secondary'">
          {{ renderCell(column, row.item[column.key], column.key, row.item) }}
        </b-badge>
      </template>
    </b-table>
    <div
      v-if="pageSizeSelectable"
      class="d-flex flex-wrap justify-content-between align-items-center table-footer"
    >
      <b-pagination
        :value="pagination.current"
        :per-page="pagination.pageSize"
        :total-rows="pagination.totalSize"
        class="mb-2 mb-md-0"
        @change="$emit('page-change', $event)"
      ></b-pagination>
      <div class="d-flex align-items-center page-size-control mb-2 mb-md-0">
        <span class="text-muted mr-2 text-nowrap">{{ pagination.totalSize }} data</span>
        <b-form-select
          :value="pagination.pageSize"
          :options="pageSizeOptions"
          size="sm"
          style="width: auto;"
          @change="$emit('page-size-change', $event)"
        />
      </div>
    </div>
    <b-pagination
      v-else
      :value="pagination.current"
      :per-page="pagination.pageSize"
      :total-rows="pagination.totalSize"
      @change="$emit('page-change', $event)"
    ></b-pagination>
  </div>
</template>

<script>
import ButtonOptions from '@/components/directives/button-options';

export default {
  name: 'MasterTable',
  components: { ButtonOptions },
  props: {
    access: { type: String, required: true },
    columns: { type: Array, required: true },
    rows: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    pagination: { type: Object, required: true },
    emptyText: { type: String, default: 'No data available.' },
    pageSizeSelectable: { type: Boolean, default: false },
    pageSizeOptions: { type: Array, default: () => [] },
    allowDelete: { type: Boolean, default: true },
  },
  computed: {
    hasRowActions() {
      return this.canUpdate(this.access)
        || (this.allowDelete && this.canDelete(this.access))
        || !!this.$scopedSlots['row-actions'];
    },
    tableFields() {
      const fields = this.columns.map((col) => {
        const field = {
          key: col.key,
          label: col.label,
          class: `text-${col.align || 'left'}`,
        };
        if (col.sortable) field.sortable = true;
        if (col.width) field.thStyle = { width: col.width };
        if (col.tdClass) field.tdClass = col.tdClass;
        if (col.formatter || col.map) {
          field.formatter = (value, key, item) => this.renderCell(col, value, key, item);
        }
        return field;
      });
      if (this.hasRowActions) {
        fields.push({ key: 'action', label: '', class: 'text-center' });
      }
      return fields;
    },
    badgeColumns() {
      return this.columns.filter((column) => column.type === 'badge');
    },
  },
  methods: {
    renderCell(col, value, key, item) {
      if (value === null || value === undefined || value === '') return '-';
      if (col.map) return col.map[value] !== undefined ? col.map[value] : value;
      return col.formatter ? col.formatter(value, key, item) : value;
    },
  },
};
</script>
